import { SEOHead } from '../components/SEOHead';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ScrollReveal';
import { CTABanner } from '../components/CTABanner';
import { ArrowUpRight } from 'lucide-react';

const FOUNDER_IMG = 'https://customer-assets.emergentagent.com/job_thriveit-preview/artifacts/sa9twqhg_RAM02100.JPG';

const numbers = [
  { value: '40+', label: 'Brands Worked With' },
  { value: '1500+', label: 'Creator Collaborations' },
  { value: '5x', label: 'ROAS Delivered on Meta Ads' },
  { value: '5+', label: 'Years Experience' },
  { value: '#1', label: 'Amazon Best Seller for Client' },
];

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="About Thriveit Media | AI Marketing Agency Bengaluru | Satyam Dokania"
        description="Thriveit Media is an AI-first marketing agency founded by Satyam Dokania. We help D2C brands grow through influencer marketing, AI UGC, and content."
        canonical="https://thriveitmedia.in/about"
      />

      {/* Hero */}
      <section className="py-32 md:py-40 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-6">About Us</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none text-[#F5F5F0] max-w-4xl">
              We're Thriveit Media — Built to Make Content That Performs
            </h1>
          </ScrollReveal>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <ScrollReveal>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-8">Our Story</h2>
              <p className="text-base md:text-lg font-body font-light text-[#888880] leading-relaxed">
                Thriveit Media was built out of frustration. We kept watching D2C brands spend lakhs on traditional shoots that took weeks to deliver — only to produce content that was outdated, overproduced, and underperforming in the feed. Polished content was losing to authentic content. Big influencers were losing to smaller, more trusted creators. And brands that needed fresh creatives every week were stuck in slow, expensive production cycles. We built Thriveit Media to solve all three of these problems — using AI and creator-led production to help D2C brands get better content, faster, at a fraction of the traditional cost.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.15}>
              <div className="bg-[#0a0a0a] border border-white/5 p-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF5C35]/5" />
                <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-8">Founded on a Simple Idea</p>
                <p className="text-2xl md:text-3xl font-heading font-bold text-[#F5F5F0] leading-tight">
                  "Content should be built to perform in the feed — not just look good in a deck."
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-4">
              <ScrollReveal>
                <div className="bg-[#111111] border border-white/5 aspect-[3/4] flex items-end justify-center relative overflow-hidden">
                  <img src={FOUNDER_IMG} alt="Satyam Dokania - Founder of Thriveit Media" className="w-full h-full object-cover object-top" />
                </div>
              </ScrollReveal>
            </div>
            <div className="lg:col-span-8">
              <ScrollReveal delay={0.1}>
                <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-4">Meet the Founder</h2>
                <p className="text-xl font-heading font-bold text-[#FFE500] mb-6">Satyam Dokania — Founder, Thriveit Media</p>
                <p className="text-base md:text-lg font-body font-light text-[#888880] leading-relaxed mb-6">
                  Satyam Dokania has 5+ years of experience in social media marketing, influencer campaigns, content strategy, and performance marketing. He has worked with 40+ brands across India, the US, and Canada — helping D2C companies grow on Instagram, run high-performing Meta ad campaigns, and build influencer campaigns that drive real sales. He founded Thriveit Media in 2021 with a clear focus: help brands produce better content, faster and leaner, using AI and creator-led production. Based in Bengaluru, Thriveit Media now works with D2C brands, ecommerce companies, and growing businesses globally.
                </p>
                <div className="flex gap-4">
                  <a
                    href="https://www.linkedin.com/in/satyamdokania/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="founder-linkedin"
                    className="inline-flex items-center gap-2 text-sm font-body text-[#F5F5F0] hover:text-[#FF5C35] transition-colors"
                  >
                    LinkedIn <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                  <a
                    href="https://www.instagram.com/aiagencyman/?hl=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="founder-instagram"
                    className="inline-flex items-center gap-2 text-sm font-body text-[#F5F5F0] hover:text-[#FF5C35] transition-colors"
                  >
                    Instagram <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section data-testid="about-numbers" className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-16">Our Numbers</h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {numbers.map((n) => (
              <StaggerItem key={n.label}>
                <div className="bg-[#0a0a0a] border border-white/5 p-8 text-center">
                  <p className="text-4xl md:text-5xl font-heading font-extrabold text-[#FFE500]">{n.value}</p>
                  <p className="text-sm font-body text-[#888880] mt-2">{n.label}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-8">Our Approach</h2>
            <p className="text-base md:text-lg font-body font-light text-[#888880] leading-relaxed max-w-3xl">
              We are not a full-service agency that does everything average. We do four things — influencer marketing, AI UGC, real UGC, and social media content — and we do them better than anyone at our level. Every campaign and every piece of content is built around one goal: results. As an ecommerce marketing agency in Bengaluru, every decision we make is driven by what performs, not what looks impressive in a presentation.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <CTABanner heading="Work With Us" subtext="Let's discuss how Thriveit Media can help grow your brand with smarter content and campaigns." buttonText="Book a Free Call" />
    </>
  );
}
